import Combobox from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { getListProgramStudiPerPeriodePasca } from "@/services/getData";
import { DataPendaftaran } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Loader2, User, AlertCircle } from "lucide-react"; // Import icon tambahan
import { useState } from "react";
import toast from "react-hot-toast";

type InputState = {
  uk_id: string;
  id_data_pendaftaran?: string;
};

const updateDataProdi = async (data: InputState) => {
  const response = await api.post(
    "/operator-pasca/data-pendaftaran/update-pindah-prodi",
    { ...data },
  );
  return response.data;
};

export default function FormUpdateProdi({
  closeModal,
  selectedRow,
}: {
  closeModal: () => void;
  selectedRow: DataPendaftaran | null;
}) {
  const [openListProdi, setOpenListProdi] = useState(false);
  const [form, setForm] = useState<InputState>({
    uk_id: "",
  });

  const { data: dataProdiPasca, isFetching: isFetchingProdiPasca } = useQuery({
    queryKey: ["listProdiPasca", selectedRow?.id_sys_periode],
    queryFn: () =>
      getListProgramStudiPerPeriodePasca(String(selectedRow?.id_sys_periode)),
    select: (data) => {
      return data?.map((item: any) => {
        return { label: item?.unitkerja?.nama, value: item?.uk_id };
      });
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: updateDataProdi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      toast.success(data?.message || "Data berhasil diperbarui.");
      closeModal(); // Menutup modal otomatis jika sukses (optional UX improvement)
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "Terjadi kesalahan yang tidak terduga.",
      );
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.uk_id) {
      toast.error("Silakan pilih program studi tujuan.");
      return;
    }

    mutate({
      id_data_pendaftaran: String(selectedRow?.id_data_pendaftaran),
      uk_id: form.uk_id,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
      {/* --- UI IMPROVEMENT START: Informasi Siswa --- */}
      <div className="rounded-lg border bg-muted/40 p-4">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Nama Pendaftar
            </p>
            <h4 className="font-semibold text-foreground text-lg leading-none">
              {selectedRow?.data_siswa?.nama_lengkap || "-"}
            </h4>
            {/* Opsional: Tampilkan Nomor Pendaftaran jika ada di selectedRow */}
            {selectedRow?.nomor_pendaftaran && (
              <p className="text-xs text-muted-foreground">
                No. Pend: {selectedRow.nomor_pendaftaran}
              </p>
            )}
          </div>
        </div>
      </div>
      {/* --- UI IMPROVEMENT END --- */}

      <div className="space-y-2">
        <Label className="text-sm font-medium">Pindah ke Program Studi</Label>
        <Combobox
          open={openListProdi}
          setOpen={setOpenListProdi}
          isPending={isFetchingProdiPasca}
          value={form.uk_id || ""}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, uk_id: value }));
          }}
          options={dataProdiPasca}
          placeholder="Cari dan pilih prodi tujuan..."
        />
        <p className="text-[0.8rem] text-muted-foreground flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Pastikan prodi tujuan sudah sesuai.
        </p>
      </div>

      <div className="mt-4 flex justify-end gap-3 pt-2 border-t">
        <Button
          variant={"ghost"}
          type="button"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button
          type="submit"
          className="flex items-center gap-2 min-w-[120px]"
          disabled={isPending || !form.uk_id}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Menyimpan...
            </>
          ) : (
            "Simpan Perubahan"
          )}
        </Button>
      </div>
    </form>
  );
}
